<template>
    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-container" style="max-width: 600px; width: 90%;">
                <div class="modal-header">
                    <h3 class="form-header">Roberts Age-Adjusted Protocol</h3>
                    <button @click="closeModal" class="red-close-button">
                        <i class="pi pi-times" style="padding: 0;"></i>
                    </button>
                </div>
                
                <div class="modal-body" style="padding: 20px;">
                    <p style="margin-bottom: 20px; color: #555; font-size: 0.9em; line-height: 1.5;">
                        This protocol acts as an initiation nomogram for Warfarin therapy across Days 1-4. Day 1 provides a loading dose based strictly on age, and subsequent days adjust this dose based on the daily morning INR reading.
                        <br><br>
                        <strong>Clinical Guidelines:</strong> 
                        <a href="https://www.iccnetsa.org.au/Data/Sites/1/media/drugprotocols/Warfarin_Initiation_2003.doc" target="_blank" style="color: #0870DC; text-decoration: none;">South Australia (SA)</a> &nbsp;|&nbsp; 
                        <a href="https://www.health.qld.gov.au/__data/assets/pdf_file/0025/443806/warfarin-guideline.pdf" target="_blank" style="color: #0870DC; text-decoration: none;">Queensland Health</a> &nbsp;|&nbsp; 
                        <a href="https://australianprescriber.tg.org.au/articles/how-to-manage-warfarin-therapy.html" target="_blank" style="color: #0870DC; text-decoration: none;">Australian Prescriber</a>
                    </p>

                    <div style="display: flex; gap: 20px; margin-bottom: 20px;">
                        <div style="flex: 1;">
                            <label style="font-weight: bold; margin-bottom: 5px; display: block;">Patient Age (Years)</label>
                            <InputNumber v-model="age" style="width: 100%;" inputStyle="width: 100%;" />
                        </div>
                        <div style="flex: 1;">
                            <label style="font-weight: bold; margin-bottom: 5px; display: block;">Treatment Day</label>
                            <Dropdown v-model="currentDay" :options="dayOptions" optionLabel="label" optionValue="value" style="width: 100%;" placeholder="Select Day" />
                        </div>
                    </div>

                    <div v-if="currentDay > 1" style="margin-bottom: 20px;">
                        <label style="font-weight: bold; margin-bottom: 5px; display: block;">Today's Morning INR</label>
                        <InputNumber v-model="dailyInr" :minFractionDigits="1" :maxFractionDigits="1" style="width: 100%;" inputStyle="width: 100%;" />
                    </div>

                    <div style="background-color: #f8f9fa; border-radius: 8px; padding: 20px; text-align: center; border: 1px solid #e9ecef; margin-top: 30px;">
                        <h4 style="margin: 0 0 10px 0; color: #0870DC;">Recommended Dose</h4>
                        <div v-if="recommendedDose !== null" style="font-size: 2em; font-weight: bold; color: #28a745;">
                            {{ recommendedDose }} <span style="font-size: 0.5em; color: #666;">mg</span>
                        </div>
                        <div v-else-if="currentDay > 1 && !dailyInr" style="color: #dc3545; font-weight: bold;">
                            Please enter today's INR reading.
                        </div>
                        <div v-else style="color: #666; font-style: italic;">
                            Pending inputs...
                        </div>
                        
                        <div v-if="holdDoseMessage" style="color: #dc3545; margin-top: 10px; font-weight: bold; font-size: 0.9em;">
                            {{ holdDoseMessage }}
                        </div>
                        <div v-if="computedInitialDose !== null && currentDay > 1" style="margin-top: 10px; font-size: 0.8em; color: #888;">
                            Baseline Loading Dose (from Age): {{ computedInitialDose }} mg
                        </div>
                    </div>
                </div>

                <div class="modal-footer" style="padding: 15px 20px; display: flex; justify-content: flex-end;">
                    <button class="regular-button" @click="closeModal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "RobertsProtocolModal",
    props: {
        initialAge: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            age: this.initialAge,
            currentDay: 1,
            dailyInr: null,
            dayOptions: [
                { label: 'Day 1', value: 1 },
                { label: 'Day 2', value: 2 },
                { label: 'Day 3', value: 3 },
                { label: 'Day 4', value: 4 }
            ],
            holdDoseMessage: ''
        };
    },
    computed: {
        computedInitialDose() {
            if (!this.age) return null;
            if (this.age <= 50) return 10;
            if (this.age >= 51 && this.age <= 65) return 9;
            if (this.age >= 66 && this.age <= 80) return 7.5;
            if (this.age > 80) return 6;
            return null;
        },
        recommendedDose() {
            if (!this.age) return null;
            
            const initialDose = this.computedInitialDose;
            
            if (this.currentDay === 1) {
                return initialDose;
            }
            
            if (this.currentDay > 1) {
                if (!this.dailyInr) return null;
                
                if (this.dailyInr >= 1.1 && this.dailyInr <= 1.3) {
                    return initialDose; // 100%
                } else if (this.dailyInr >= 1.4 && this.dailyInr <= 3.0) {
                    return initialDose * 0.50; // 50%
                } else if (this.dailyInr >= 3.1 && this.dailyInr <= 3.5) {
                    return initialDose * 0.25; // 25%
                } else if (this.dailyInr > 3.5) {
                    return 0; // Hold dose
                }
                
                
                // If INR < 1.1, the protocol generally implies maintaining or slight increase, 
                // but strictly following the provided rules, it just isn't covered by a reduction bracket.
                // Assuming 100% if below 1.1 based on typical initiation workflows.
                return initialDose; 
            }
            return null;
        }
    },
    watch: {
        initialAge(newVal) {
            this.age = newVal;
        },
        currentDay(newVal) {
            if (newVal === 1) {
                this.dailyInr = null;
            }
            if (newVal > 1 && this.dailyInr > 3.5) {
                this.holdDoseMessage = "Hold dose until INR drops below 3.5. Then restart at 50% less than previous dose.";
            } else {
                this.holdDoseMessage = '';
            }
        },
        dailyInr(newVal) {
            if (this.currentDay > 1 && newVal > 3.5) {
                this.holdDoseMessage = "Hold dose until INR drops below 3.5. Then restart at 50% less than previous dose.";
            } else {
                this.holdDoseMessage = '';
            }
        }
    },
    methods: {
        closeModal() {
            this.$emit('close');
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../assets/css/main.scss";
</style>
